// eslint-disable-next-line jira/import-whitelist
import { addSpanToAll } from '@atlaskit/react-ufo/interaction-metrics';
// eslint-disable-next-line jira/import-whitelist
import { fetchJsonWithCache } from '@atlassian/jira-caching-layer/src/utils/cache-proxies/fetch-json.tsx';
import { setMark } from '@atlassian/jira-common-performance/src/marks.tsx';
import logger from '@atlassian/jira-common-util-logging/src/log.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
// eslint-disable-next-line jira/import-whitelist
import { expValEquals } from '@atlassian/jira-feature-experiments';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import type FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
// eslint-disable-next-line jira/import-whitelist
import { prefetchApiReadyMark } from '@atlassian/jira-providers-spa-apdex-analytics/src/marks.tsx';
import { getProjectKeyOrId } from '@atlassian/jira-router-resources-utils/src/utils/get-project-key-or-id/index.tsx';

import { checkAuthentication } from '@atlassian/jira-spa-apps-common/src/interceptors/authentication/index.tsx';

import { checkLicense } from '@atlassian/jira-spa-apps-common/src/interceptors/license/index.tsx';
import type { RouterDataContext, ResourceStoreContext } from '@atlassian/react-resource-router';

const onFetchErrorCacheHandler = (error: FetchError) => {
	logger.safeErrorWithoutCustomerData('jiraServicedeskIsItsmProjectResource', error.message);
	fireErrorAnalytics({
		meta: {
			id: 'jiraServicedeskIsItsmProjectResource',
			packageName: 'jiraSpa',
		},
		error,
		sendToPrivacyUnsafeSplunk: true,
	});

	throw error;
};

const getIsItsmProject = async (baseUrl: string, projectIdOrKey: string) => {
	if (!projectIdOrKey) {
		return false;
	}
	try {
		if (expValEquals('jira_caching_layer_milestone_1_exp', 'cohort', 'variation')) {
			const fetchStartTime = performance.now();
			const data = await fetchJsonWithCache(
				`${baseUrl}/rest/servicedesk/onboarding/1/itsm/${projectIdOrKey}/is-itsm`,
				onFetchErrorCacheHandler,
			);
			addSpanToAll(
				'fetch',
				'rest/servicedesk/onboarding/1/itsm/projectIdOrKey/is-itsm-fetchWithCache',
				[{ name: 'network' }],
				fetchStartTime,
				performance.now(),
			);
			setMark(prefetchApiReadyMark);
			return data;
		}
		const fetchStartTime = performance.now();
		const data = await fetchJson(
			`${baseUrl}/rest/servicedesk/onboarding/1/itsm/${projectIdOrKey}/is-itsm`,
		);
		addSpanToAll(
			'fetch',
			'rest/servicedesk/onboarding/1/itsm/projectIdOrKey/is-itsm',
			[{ name: 'network' }],
			fetchStartTime,
			performance.now(),
		);
		setMark(prefetchApiReadyMark);
		return data;

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		logger.safeErrorWithoutCustomerData('jiraServicedeskIsItsmProjectResource', error.message);
		fireErrorAnalytics({
			meta: {
				id: 'jiraServicedeskIsItsmProjectResource',
				packageName: 'jiraSpa',
			},
			error,
			sendToPrivacyUnsafeSplunk: true,
		});

		throw error;
	}
};

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default ({ match }: RouterDataContext, { tenantContext }: ResourceStoreContext) => {
	// Since this will be mostly used for onboarding, there are some special pages that either does not have TenantContextContainer in the react tree or somehow not
	// getting tenantContext populated properly, add this check to avoid flooding sentry
	if (tenantContext === undefined) {
		throw new Error('tenantContext is missing');
	}

	// Early exit on these cases and return a valid response. We don't want to flood sentry.
	if (!checkAuthentication(tenantContext) || !checkLicense(tenantContext, 'servicedesk')) {
		throw new Error('Authentication or licence missing');
	}
	const projectIdOrKey = getProjectKeyOrId(match);
	return getIsItsmProject(tenantContext.baseUrl, projectIdOrKey);
};
